<template>
  <div class="invoice-panel">
    <!-- NOVO: Stolna napomena indicator -->
    <v-alert
      v-if="tableNote"
      dense
      prominent
      border="left"
      colored-border
      color="orange darken-1"
      class="table-note-alert mb-2 touchscreen-alert"
      @click="openEditTableNote">
      <v-row align="center" class="ma-0">
        <v-icon color="orange darken-2" size="28" class="mr-3">
          mdi-note-text
        </v-icon>
        <div class="flex-grow-1">
          <div class="font-weight-bold text-uppercase mb-1" style="font-size: 13px;">
            NAPOMENA ZA STOL
          </div>
          <div style="font-size: 15px; line-height: 1.4;">
            {{ tableNote }}
          </div>
        </div>
        <v-btn
          icon
          large
          color="orange darken-2"
          @click.stop="openEditTableNote">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </v-row>
    </v-alert>

    <!-- Invoice Items Table -->
    <v-data-table v-if="!ordersView"
      class="invoice-table"
      :height="tableHeight"
      fixed-header
      :headers="filteredHeaders"
      :items="invoice.items"
      :items-per-page="-1"
      hide-default-footer
      :item-class="getRowClass"
      @click:row="openItemsDiscountSelectionDialog">
      <template v-slot:top>
        <v-toolbar dense
          flat
          outlined
          style="border-left: 0; height: 3.4rem"><span style="
              font-weight: bold;
              text-transform: capitalize;
              font-size: 16px;
              padding: 0 20px 0 0;
            ">{{
              selectedTable.user_name ? selectedTable.user_name : ""
            }}</span>
          <v-spacer></v-spacer>
          <v-toolbar-title>
            <div style="
                padding-top: 1.5rem;
                padding-right: 0.5rem;
                padding-left: 2rem;
                display: flex;
                align-items: center;
                gap: 0.5rem;
              ">
              <v-switch v-model="switchOnOrdersTable"
                @change="onOrdersChange"></v-switch>

              <!-- Table Notes Indicator Badge -->
              <v-tooltip bottom v-if="!ordersView && hasTableNotes">
                <template v-slot:activator="{ on, attrs }">
                  <v-badge
                    :content="tableNotesCount"
                    color="info"
                    overlap
                    offset-x="22"
                    offset-y="22"
                    style="margin-top: -1.2rem;">
                    <v-btn
                      icon
                      color="info"
                      v-bind="attrs"
                      v-on="on"
                      @click="viewAllTableNotes">
                      <v-icon size="28">mdi-note-text</v-icon>
                    </v-btn>
                  </v-badge>
                </template>
                <span>Prikaži stolne napomene ({{ tableNotesCount }})</span>
              </v-tooltip>

              <!-- Cart Discount Menu -->
              <v-menu
                v-if="!ordersView"
                offset-y
                content-class="general-menu-dropdown"
                nudge-bottom="8">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    large
                    v-bind="attrs"
                    v-on="on"
                    class="menu-icon-btn"
                    style="margin-top: -1.2rem;">
                    <v-icon size="28" color="#3989C9">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list class="general-menu-list">
                  <v-list-item @click="onCartDiscountClick" class="menu-list-item">
                    <v-list-item-icon class="menu-item-icon">
                      <v-icon color="#3989C9">{{ hasCartDiscounts ? 'mdi-percent-off' : 'mdi-percent' }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="menu-item-title">
                      {{ hasCartDiscounts ? 'Ukloni popust' : 'Dodaj popust' }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="generalMenuClick('addNoteDialogOpen')" class="menu-list-item">
                    <v-list-item-icon class="menu-item-icon">
                      <v-icon color="#3989C9">mdi-note-text</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="menu-item-title">
                      Dodaj napomenu
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-toolbar-title>
        </v-toolbar>
      </template>
      <template #[`item.name`]="{ item }">
        <div v-if="!showEdit(item)"
          class="invoice-row"
          style="min-width: 15rem; padding-left: 0px; padding-right: 0px"
          :style="{ 'text-decoration': 'none' }">
          {{ item.name }}
          <v-icon v-if="showPencil(item)"
            class="ml-2">mdi-pencil</v-icon>
        </div>
        <div v-else>
          <v-text-field autofocus
            class="invoice-row"
            v-model="item.name"
            append-icon="mdi-check"
            @click:append="finishEdit(item)"
            @keypress.enter="finishEdit(item)">
          </v-text-field>
        </div>
      </template>
      <template #[`item.selling_unit`]="{ item }">
        <div class="invoice-row"
          style="min-width: 5rem; padding-left: 0px; padding-right: 0px">
          {{ item.selling_unit }}
        </div>
      </template>
      <template #[`item.unitPrice`]="{ item }">
        <div class="invoice-row"
          style="min-width: 3rem; padding-left: 0px; padding-right: 0px">
          {{ $options.filters.money(item.unitPrice, 100, currency) }}
        </div>
      </template>
      <template #[`item.discountedUnitPrice`]="{ item }">
        <div class="invoice-row"
          style="min-width: 3rem; padding-left: 0px; padding-right: 0px">
          {{
            $options.filters.money(item.discountedUnitPrice, 100, currency)
          }}
        </div>
      </template>
      <template #[`item.total`]="{ item }">
        <div class="invoice-row"
          style="min-width: 3rem; padding-left: 0px; padding-right: 0px">
          {{ $options.filters.money(item.total, 100, currency) }}
        </div>
      </template>
      <template #[`item.quantity`]="{ item }">
        <div style="
            max-width: 2rem;
            height: 24px !important;
            text-align: center;
          "
          class="invoice-row"
          :disabled="item.type && item.type === 'menu-product'">
          {{ item.quantity }}
        </div>
      </template>
      <template #[`item.pendingQuantity`]="{ item }">
        <div style="display: flex; justify-content: flex-start; align-items: center; padding-left: 8px;">
          <v-chip v-if="itemPendingQuantities[item.id] && itemPendingQuantities[item.id] > 0"
            small
            color="success"
            dark
            style="min-width: 45px; height: 26px; font-weight: 900; font-size: 12px; display: flex; justify-content: center; align-items: center;">
            {{ itemPendingQuantities[item.id] }}
          </v-chip>
        </div>
      </template>
      <template #[`item.controls`]="{ item }">
        <div class="cart-action-icons">
          <v-btn
            icon
            small
            color="success"
            class="cart-action-btn"
            :disabled="item.type === 'menu-product'"
            @click.stop="onRowBtnClick(item, 'increment')">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            color="warning"
            class="cart-action-btn"
            :disabled="item.type === 'menu-product'"
            @click.stop="onRowBtnClick(item, 'decrement')">
            <v-icon>mdi-minus</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            color="error"
            class="cart-action-btn"
            :disabled="item.type === 'menu-product'"
            @click.stop="onRowBtnClick(item, 'remove')">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Orders Table (Alternative View) -->
    <v-data-table v-if="selectedTable && ordersView"
      :headers="orderHeaders"
      :items="reversedOrders"
      :single-expand="singleExpand"
      :expanded.sync="expanded"
      :height="tableHeight"
      hide-default-footer
      fixed-header
      :items-per-page="-1"
      item-key="id"
      show-expand
      class="invoice-table">
      <template v-slot:top>
        <v-toolbar dense
          flat
          outlined
          rounded
          shaped>
          <span style="
              font-weight: bold;
              text-transform: capitalize;
              font-size: 16px;
              padding: 0 20px 0 0;
            ">{{
              selectedTable.user_name ? selectedTable.user_name : ""
            }}</span>
          <v-spacer></v-spacer>
          <v-toolbar-title>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div v-on="on"
                  style="
                    padding-top: 1.5rem;
                    padding-right: 2rem;
                    padding-left: 2rem;
                  ">
                  <v-switch v-model="switchOnOrdersTable"
                    @change="onOrdersChange"></v-switch>
                </div>
              </template>
              <span>{{
                switchOnOrdersTable ? "Narudžbe" : "Blagajna"
              }}</span>
            </v-tooltip>
          </v-toolbar-title>
        </v-toolbar>
      </template>
      <template v-slot:expanded-item="{ item }">
        <td :colspan="orderHeaders.length">
          <!-- Display courses if they exist -->
          <template v-if="item.courses && item.courses.length > 0">
            <div v-for="(course, courseIndex) in item.courses" :key="`course-${item.id}-${courseIndex}`">
              <v-row flat class="d-flex grey lighten-3">
                <v-col cols="12" class="d-flex justify-center pt-3 pb-2">
                  <strong>{{ course.name }}</strong>
                </v-col>
              </v-row>
              <v-row v-for="it in course.items"
                :key="`order-${item.id}-course-${courseIndex}-${it.id}`"
                flat
                class="d-flex">
                <v-col cols="4"
                  class="d-flex justify-center pt-5 pl-5">{{
                    it.name
                  }}</v-col>
                <v-col cols="4"
                  class="d-flex justify-center pt-5 pr-5 pl-5">{{
                    it.quantity
                  }}</v-col>
                <v-col cols="4"
                  class="d-flex justify-center pt-5">{{
                    $options.filters.money(it.unitPrice, 100, currency)
                  }}</v-col>
              </v-row>
            </div>
          </template>

          <!-- Display order_items if courses don't exist (backward compatibility) -->
          <v-row v-else
            v-for="it in item.order_items"
            :key="`order-${item.id}-${it.id}`"
            flat
            class="d-flex">
            <v-col cols="4"
              class="d-flex justify-center pt-5 pl-5">{{
                it.name
              }}</v-col>
            <v-col cols="4"
              class="d-flex justify-center pt-5 pr-5 pl-5">{{
                it.quantity
              }}</v-col>
            <v-col cols="4"
              class="d-flex justify-center pt-5">{{
                $options.filters.money(it.discountedUnitPrice, 100, currency)
              }}</v-col>
          </v-row>
        </td>
      </template>
      <template v-slot:item.created_at="{ item }">
        <span v-if="item.created_at || item.created">
          {{ formatTimestamp(item.created_at || item.created) }}
        </span>
        <span v-else class="grey--text text--lighten-1" style="font-size: 12px;">
          (stari order)
        </span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              icon
              small
              color="primary"
              :disabled="item.order_status !== 'ORDERED'"
              @click.stop="manualPrintOrder(item)">
              <v-icon small>mdi-printer</v-icon>
            </v-btn>
          </template>
          <span>{{ item.order_status === 'ORDERED' ? 'Printaj narudžbu' : 'Samo ORDERED narudžbe se mogu printati' }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import EventBus from '@/plugins/event-bus'
import { df } from '@/plugins/firebase'
import state from '@/state'
import { printOrder } from '@/libs/bixolon/order'

export default {
  name: 'InvoicePanel',
  props: {
    invoice: {
      type: Object,
      required: true
    },
    ordersView: {
      type: Boolean,
      default: false
    },
    selectedTable: {
      type: Object,
      default: () => ({})
    },
    cartConfiguration: {
      type: Object,
      default: undefined
    },
    showShortcutBadges: {
      type: Boolean,
      default: false
    },
    keyboardShortcutsEnabled: {
      type: Boolean,
      default: false
    },
    currency: {
      type: String,
      default: 'EUR'
    },
    itemPendingQuantities: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      expanded: [],
      singleExpand: false,
      switchOnOrdersTable: false,
      // OPTIMIZATION: Object.freeze() for static data arrays - reduces reactive overhead
      originalHeaders: Object.freeze([
        Object.freeze({
          text: 'Rb.',
          align: 'start',
          sortable: false,
          value: 'rbr'
        }),
        Object.freeze({ text: 'Naziv', value: 'name', sortable: false }),
        Object.freeze({ text: 'Količina', value: 'quantity', sortable: false }),
        Object.freeze({ text: 'Pending kol.', value: 'pendingQuantity', sortable: false }),
        Object.freeze({ text: 'Mj. jedinica', value: 'selling_unit', sortable: false }),
        Object.freeze({ text: 'Jed. cijena', value: 'unitPrice', sortable: false }),
        Object.freeze({ text: 'Jed. cijena s pop.', value: 'discountedUnitPrice', sortable: false }),
        Object.freeze({ text: 'Ukupno', value: 'total', sortable: false }),
        Object.freeze({ text: '', value: 'controls', sortable: false, align: 'end' })
      ]),
      orderHeaders: Object.freeze([
        Object.freeze({
          text: 'Vrijeme',
          align: 'start',
          value: 'created_at',
          width: '140px'
        }),
        Object.freeze({
          text: 'Rb.',
          align: 'start',
          value: 'order_number'
        }),
        Object.freeze({ text: 'Izmjenio', value: 'added_by_name' }),
        Object.freeze({ text: 'Status', value: 'order_status' }),
        Object.freeze({ text: 'Akcije', value: 'actions', sortable: false, align: 'center' }),
        Object.freeze({ text: '', value: 'data-table-expand' })
      ]),
      itemMouseover: [],
      itemEdit: [],
      savedName: '',
      tableHeight: 0,
      generalMenu: [],
      lastInsertBy: '',
      // OPTIMIZATION: Debounced calculateHeights
      debouncedCalculateHeights: null,
      // OPTIMIZATION: Header configuration mappings for O(1) lookup
      headerConfigMapping: Object.freeze({
        rb: 'rbr',
        name: 'name',
        quantity: 'quantity',
        pending_quantity: 'pendingQuantity',
        unit: 'selling_unit',
        single_price: 'unitPrice',
        single_price_discount: 'discountedUnitPrice',
        total: 'total',
        action_icons: 'controls'
      }),
      headerValueToConfig: null, // Will be populated in created()
      focusedRowIndex: -1 // For keyboard navigation
    }
  },
  computed: {
    // PERFORMANCE: Replaced deep watcher with computed property for headers filtering
    filteredHeaders () {
      // Guard: Return all headers if cartConfiguration is not loaded yet
      if (!this.cartConfiguration) {
        return this.originalHeaders
      }
      return this.filterHeaders(this.originalHeaders, this.cartConfiguration)
    },
    // Reversed orders - latest added on top, filter out empty orders
    reversedOrders () {
      if (!this.selectedTable || !this.selectedTable.orders) {
        return []
      }
      return [...this.selectedTable.orders]
        .filter(order => {
          // Prihvati order ako ima order_items ILI courses
          const hasOrderItems = order.order_items && order.order_items.length > 0
          const hasCourses = order.courses && order.courses.length > 0
          return hasOrderItems || hasCourses
        })
        .reverse()
    },
    // Check if any cart items have discounts
    hasCartDiscounts () {
      if (!this.invoice || !this.invoice.items) {
        return false
      }
      return this.invoice.items.some(item =>
        item.discount && item.discount.id
      )
    },
    // NOVO: Dohvati stolnu napomenu iz selectedTable
    tableNote () {
      if (!this.selectedTable || !this.selectedTable.notes) {
        return null
      }

      const stolNote = this.selectedTable.notes.find(
        note => note.unitId === 'stol' ||
                note.unitName === 'Stol' ||
                note.isTableNote === true
      )

      return stolNote ? stolNote.message : null
    },
    // NOVO: Provjeri da li ima stolnih napomena (trenutna + povijest)
    hasTableNotes () {
      // Provjeri trenutnu stolnu napomenu
      if (this.tableNote) return true

      // Provjeri povijest - da li ima stolnih napomena u prošlim orderima
      if (this.selectedTable && this.selectedTable.orders) {
        return this.selectedTable.orders.some(order => {
          if (!order.notes) return false
          return order.notes.some(note =>
            note.unitId === 'stol' ||
            note.unitName === 'Stol' ||
            note.isTableNote === true
          )
        })
      }

      return false
    },
    // NOVO: Broj stolnih napomena (trenutna + sve iz povijesti)
    tableNotesCount () {
      let count = 0

      // Trenutna napomena
      if (this.tableNote) count++

      // Povijest napomena
      if (this.selectedTable && this.selectedTable.orders) {
        this.selectedTable.orders.forEach(order => {
          if (order.notes) {
            order.notes.forEach(note => {
              if (note.unitId === 'stol' || note.unitName === 'Stol' || note.isTableNote === true) {
                count++
              }
            })
          }
        })
      }

      return count
    }
  },
  watch: {
    // OPTIMIZATION: Watch ordersView to sync with switchOnOrdersTable
    ordersView: {
      immediate: true,
      handler (newVal) {
        this.switchOnOrdersTable = newVal
      }
    }
  },
  methods: {
    // Scroll focused row into view
    scrollToFocusedRow () {
      this.$nextTick(() => {
        const table = this.$el.querySelector('.v-data-table__wrapper')
        if (!table) return

        const rows = table.querySelectorAll('tbody tr')
        if (!rows || this.focusedRowIndex < 0 || this.focusedRowIndex >= rows.length) return

        const focusedRow = rows[this.focusedRowIndex]
        if (!focusedRow) return

        // Get thead height to account for fixed header
        const thead = table.querySelector('thead')
        const headerHeight = thead ? thead.offsetHeight : 0

        // Get row position relative to table container
        const tableTop = table.scrollTop
        const tableHeight = table.clientHeight
        const rowTop = focusedRow.offsetTop
        const rowHeight = focusedRow.offsetHeight

        // Calculate if row is outside visible area (accounting for header)
        const rowBottom = rowTop + rowHeight
        const visibleTop = tableTop + headerHeight
        const visibleBottom = tableTop + tableHeight

        // Scroll only the table container, not the entire page
        if (rowTop < visibleTop) {
          // Row is above visible area - scroll up (leave space for header)
          table.scrollTop = rowTop - headerHeight
        } else if (rowBottom > visibleBottom) {
          // Row is below visible area - scroll down
          table.scrollTop = rowBottom - tableHeight
        }
      })
    },
    // Keyboard navigation handler for cart
    handleCartKeyPress (e) {
      // Only handle if shortcuts are enabled, there are items in cart and not in ordersView
      if (!this.keyboardShortcutsEnabled || !this.invoice.items || this.invoice.items.length === 0 || this.ordersView) {
        return
      }

      // Don't handle if a modal/dialog is open (check for v-dialog)
      const activeDialog = document.querySelector('.v-dialog--active')
      if (activeDialog) {
        return
      }

      // Arrow Down - move to next item
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (this.focusedRowIndex < this.invoice.items.length - 1) {
          this.focusedRowIndex++
          this.scrollToFocusedRow()
        }
      }

      // Arrow Up - move to previous item
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (this.focusedRowIndex > 0) {
          this.focusedRowIndex--
          this.scrollToFocusedRow()
        } else if (this.focusedRowIndex === -1 && this.invoice.items.length > 0) {
          // Start from first item if nothing is focused
          this.focusedRowIndex = 0
          this.scrollToFocusedRow()
        }
      }

      // X - remove all of focused item
      if ((e.key === 'x' || e.key === 'X') && this.focusedRowIndex >= 0) {
        e.preventDefault()
        const item = this.invoice.items[this.focusedRowIndex]
        if (item && item.type !== 'menu-product') {
          this.onRowBtnClick(item, 'remove')
          // Adjust focus if removed last item
          if (this.focusedRowIndex >= this.invoice.items.length) {
            this.focusedRowIndex = this.invoice.items.length - 1
          }
        }
      }

      // + - increment quantity
      if (e.key === '+' && this.focusedRowIndex >= 0) {
        e.preventDefault()
        const item = this.invoice.items[this.focusedRowIndex]
        if (item && item.type !== 'menu-product') {
          this.onRowBtnClick(item, 'increment')
        }
      }

      // - - decrement quantity
      if (e.key === '-' && this.focusedRowIndex >= 0) {
        e.preventDefault()
        const item = this.invoice.items[this.focusedRowIndex]
        if (item && item.type !== 'menu-product') {
          this.onRowBtnClick(item, 'decrement')
        }
      }
    },
    // Get row class for focused item
    getRowClass (item) {
      const index = this.invoice.items.indexOf(item)
      return index === this.focusedRowIndex ? 'focused-cart-row' : ''
    },
    // OPTIMIZATION: Extract updateGeneralMenu method
    updateGeneralMenu () {
      this.generalMenu = [
        { text: 'Ukloni artikle', method: 'removeProducts', icon: 'mdi-delete' },
        { text: 'Dodaj napomenu', method: 'addNoteDialogOpen', icon: 'mdi-delete' }
      ]
    },
    filterHeaders (headers, config) {
      // OPTIMIZATION: O(1) lookup using pre-computed reverse mapping
      return headers.filter(header => {
        const configKey = this.headerValueToConfig[header.value]
        return configKey ? config[configKey] : true
      })
    },
    onRowBtnClick (item, operation) {
      this.$emit('row-btn-click', item, operation)
    },
    onChangeQuantity (item) {
      this.$emit('change-quantity', item)
    },
    onNameMouseover (item) {
      if (this.itemMouseover.length === 0) {
        this.itemMouseover.push(item)
      } else {
        this.itemMouseover = []
      }
    },
    onNameClick (item) {
      if (this.itemEdit.length === 0) {
        this.savedName = item.name
        this.itemMouseover = []
        this.itemEdit.push(item)
      }
      this.$emit('open-edit-name-dialog', item)
    },
    showEdit (item) {
      return this.itemEdit.length > 0 && this.itemEdit[0].id === item.id
    },
    showPencil (item) {
      return this.itemMouseover.length > 0 && this.itemMouseover[0].id === item.id
    },
    finishEdit (item) {
      if (item.name.length === 0) {
        item.name = this.savedName
      }
      this.itemEdit = []
    },
    openDiscountSelectionDialog () {
      this.$emit('open-discount-dialog')
    },
    openItemsDiscountSelectionDialog (e, { item }) {
      this.$emit('open-item-discount-dialog', item)
    },
    generalMenuClick (action) {
      this.$emit('general-menu-click', action)
    },
    itemMenuClick (item, action) {
      this.$emit('item-menu-click', item, action)
    },
    onOrdersChange () {
      EventBus.$emit('set-orders-view', this.switchOnOrdersTable)
    },
    onCartDiscountClick () {
      this.$emit('cart-discount-click', this.hasCartDiscounts)
    },
    // NOVO: Otvori edit stol napomene
    openEditTableNote () {
      this.$emit('open-table-note-dialog')
    },
    // NOVO: Otvori pregled svih stolnih napomena (trenutna + povijest)
    viewAllTableNotes () {
      this.$emit('view-all-table-notes')
    },
    calculateHeights () {
      let elementsTotalHeight = 0
      let tilesTotalHeight = 0
      const rowHeight = 58
      const headerHeight = 48
      const spacerHeight = 0
      const tableHeaderHeight = 48
      const totalRowHeight = 64 // Height of total-row footer (4rem = 64px)
      const nesto = 58

      // Calculate based on panel rows count from state
      const panelRows = state.getPanelRows() || 6
      tilesTotalHeight = panelRows * rowHeight
      elementsTotalHeight = nesto + tilesTotalHeight + headerHeight + spacerHeight + tableHeaderHeight + totalRowHeight
      this.tableHeight = window.innerHeight - elementsTotalHeight
    },
    async loadConfiguration () {
      const conf = await df.doc(`cash_registers/${state.getCashRegister().id}/cart_configuration/configuration`).get()

      if (conf && conf.data()) {
        this.$emit('update:cart-configuration', conf.data())
      }
    },
    formatTimestamp (timestamp) {
      if (!timestamp) return '-'

      // Handle Firebase Timestamp, milliseconds, or Unix seconds
      let date
      if (timestamp.toDate) {
        // Firebase Timestamp
        date = timestamp.toDate()
      } else if (timestamp < 10000000000) {
        // Unix timestamp in seconds (less than year 2286 in seconds)
        // Convert to milliseconds
        date = new Date(timestamp * 1000)
      } else {
        // Already in milliseconds
        date = new Date(timestamp)
      }

      // Format as HH:MM DD.MM.YYYY
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      return `${hours}:${minutes} ${day}.${month}.${year}`
    },
    async manualPrintOrder (order) {
      console.log('🖨️ Manual print triggered for order:', order)

      if (order.order_status !== 'ORDERED') {
        EventBus.$emit('show-message-box', {
          title: 'Printanje nije moguće',
          text: 'Možete printati samo narudžbe sa statusom ORDERED.',
          color: 'warning',
          actions: ['ok'],
          okBtnText: 'U redu'
        })
        return
      }

      // Get printers and org units
      const printers = []
      const printersQuery = await df.collection(`location_printers/${state.getOrderPrintLocation().id}/printers`).get()
      printersQuery.forEach(doc => {
        const printerData = { ...doc.data(), id: doc.id }
        printers.push(printerData)
      })
      console.log('🖨️ Printers found:', printers.length, printers)

      const orgUnits = []
      const orgUnitsQuery = await df.collection(`location_units/${state.getOrderPrintLocation().id}/units`).get()
      orgUnitsQuery.forEach(doc => {
        const orgUnitData = { ...doc.data(), id: doc.id }
        orgUnits.push(orgUnitData)
      })
      console.log('🖨️ Org units found:', orgUnits.length, orgUnits)

      // Group items by organizational unit
      const itemsInOrgUnits = {}
      if (order.order_items && order.order_items.length > 0) {
        order.order_items.forEach(item => {
          if (item.organizational_unit_id && item.organizational_unit_id !== '') {
            if (!itemsInOrgUnits[item.organizational_unit_id]) {
              itemsInOrgUnits[item.organizational_unit_id] = []
            }
            itemsInOrgUnits[item.organizational_unit_id].push(item)
          }
        })
      }
      console.log('🖨️ Items grouped by org unit:', itemsInOrgUnits)

      // Check other org units
      const otherUnits = []
      Object.keys(itemsInOrgUnits).forEach(unitId => {
        const orgUnit = orgUnits.find(u => u.id === unitId)
        if (orgUnit) {
          otherUnits.push(orgUnit)
        }
      })
      console.log('🖨️ Other units:', otherUnits)

      // Print for each org unit
      let printSuccess = false
      let printError = null

      for (const unitId of Object.keys(itemsInOrgUnits)) {
        const orgUnit = orgUnits.find(u => u.id === unitId)
        console.log('🖨️ Processing org unit:', unitId, orgUnit)

        if (!orgUnit) {
          console.log('🔴 Org unit not found for:', unitId)
          continue
        }

        const printer = printers.find(p => p.id === orgUnit.printer || p.name === orgUnit.printer)
        console.log('🖨️ Printer for org unit:', printer)

        if (!printer) {
          console.log('🔴 Printer not found for org unit:', orgUnit.printer)
          printError = `Printer nije pronađen za org unit: ${orgUnit.name || unitId}`
          continue
        }

        let note = null
        if (order.notes) {
          note = order.notes.find(n => n.unitId === orgUnit.id)
        }

        console.log('🖨️ Calling printOrder with:', {
          items: itemsInOrgUnits[unitId].length,
          printer: printer.name,
          table: this.selectedTable.name,
          order: order.id,
          orgUnit: orgUnit.name
        })

        try {
          state.setPosPrinterSettings(2)
          const result = await printOrder(itemsInOrgUnits[unitId], printer, this.selectedTable, order, orgUnit, otherUnits, note)
          console.log('🖨️ Print result:', result)

          if (result && result.success) {
            printSuccess = true
          } else {
            printError = result ? result.error : 'Nepoznata greška'
            console.log('🔴 Print failed:', printError)
          }
        } catch (err) {
          printError = err.message || 'Nepoznata greška'
          console.error('🔴 Print exception:', err)
        }
      }

      console.log('🖨️ Final result - success:', printSuccess, 'error:', printError)

      // Show result
      if (printSuccess) {
        // Update order status to PRINTED
        const tableOrders = [...this.selectedTable.orders]
        const orderIndex = tableOrders.findIndex(o => o.id === order.id)
        if (orderIndex !== -1) {
          tableOrders[orderIndex].order_status = 'PRINTED'
          await df.doc(`location_orders/${state.getOrderPrintLocation().id}/tables/${this.selectedTable.id}`).update({ orders: tableOrders })
        }

        EventBus.$emit('show-message-box', {
          title: 'Printanje uspješno',
          text: 'Narudžba je uspješno printana.',
          color: 'info',
          actions: ['ok'],
          okBtnText: 'U redu'
        })
      } else {
        EventBus.$emit('show-message-box', {
          title: 'Greška pri printanju',
          subtitle: this.selectedTable.name || 'Stol',
          text: `Printanje nije uspjelo: ${printError || 'Nepoznata greška'}\n\nProvjerite printer i pokušajte ponovno.`,
          color: 'error',
          actions: ['ok'],
          okBtnText: 'U redu',
          width: '500'
        })
      }
    }
  },
  created () {
    // OPTIMIZATION: Create debounced version for calculateHeights (300ms debounce for window resize)
    this.debouncedCalculateHeights = debounce(this.calculateHeights, 300)

    // OPTIMIZATION: Create reverse mapping for O(1) header lookup
    this.headerValueToConfig = Object.freeze(
      Object.entries(this.headerConfigMapping).reduce((acc, [key, value]) => {
        acc[value] = key
        return acc
      }, {})
    )

    // Initialize general menu
    this.updateGeneralMenu()

    // OPTIMIZATION: Use debounced version for window resize
    window.addEventListener('resize', this.debouncedCalculateHeights)
  },
  mounted () {
    // Calculate heights on mount
    this.calculateHeights()

    // Sync ordersView with switchOnOrdersTable
    this.switchOnOrdersTable = this.ordersView

    // Register keyboard event listener for cart navigation
    document.addEventListener('keydown', this.handleCartKeyPress)
  },
  beforeDestroy () {
    // OPTIMIZATION: Cancel debounced function to prevent memory leaks
    if (this.debouncedCalculateHeights) {
      this.debouncedCalculateHeights.cancel()
    }

    window.removeEventListener('resize', this.debouncedCalculateHeights)
    // Remove keyboard event listener
    document.removeEventListener('keydown', this.handleCartKeyPress)
  }
}
</script>

<style scoped>
.invoice-panel {
  width: 100%;
}

.invoice-table >>> .v-data-table__wrapper {
  overflow-y: auto;
  overflow-x: hidden;
}

.invoice-row {
  text-transform: uppercase;
  font-weight: 700;
}

/* Focused cart row highlight */
.invoice-table >>> .focused-cart-row {
  background-color: rgba(25, 118, 210, 0.12) !important;
  border-left: 3px solid #1976d2 !important;
}

/* Cart action icons */
.cart-action-icons {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  padding: 4px 0;
}

.cart-action-btn {
  min-width: 40px !important;
  min-height: 40px !important;
  border: 2px solid currentColor !important;
  border-radius: 4px !important;
}

/* Stolna napomena alert - touchscreen optimized */
.table-note-alert {
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 72px; /* Veće za touchscreen */
}

.table-note-alert:hover {
  background-color: #FFF3E0 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.table-note-alert:active {
  transform: scale(0.98); /* Touch feedback */
}

/* Touchscreen button u alert */
.table-note-alert .v-btn.v-btn--icon.v-size--large {
  min-width: 52px !important;
  min-height: 52px !important;
}

/* General Menu Icon Button */
.menu-icon-btn {
  transition: all 0.2s ease !important;
}

.menu-icon-btn:hover {
  background: rgba(57, 137, 201, 0.1) !important;
  transform: scale(1.05);
}

/* General Menu Dropdown - Glass morphism style */
.general-menu-dropdown {
  margin-top: 8px;
}

.general-menu-list {
  border-radius: 12px !important;
  overflow: hidden !important;
  padding: 0 !important;
}

.general-menu-list .v-list {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  box-shadow: 0 8px 24px rgba(57, 137, 201, 0.15) !important;
  padding: 8px !important;
  min-width: 220px !important;
}

/* Menu List Items - Touchscreen optimized */
.menu-list-item {
  min-height: 56px !important;
  padding: 12px 16px !important;
  border-radius: 8px !important;
  margin-bottom: 4px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  cursor: pointer !important;
}

.menu-list-item:last-child {
  margin-bottom: 0 !important;
}

.menu-list-item:hover {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.08) 0%,
    rgba(57, 137, 201, 0.06) 100%) !important;
  transform: translateX(4px);
  box-shadow: inset 4px 0 0 rgba(0, 179, 190, 0.6);
}

.menu-list-item:active {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.12) 0%,
    rgba(57, 137, 201, 0.1) 100%) !important;
  transform: scale(0.98);
  box-shadow: inset 4px 0 0 rgba(0, 179, 190, 0.8);
}

/* Menu Item Icon */
.menu-item-icon {
  margin-right: 16px !important;
  min-width: 40px !important;
}

.menu-item-icon .v-icon {
  font-size: 24px !important;
}

/* Menu Item Title */
.menu-item-title {
  font-size: 15px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
  white-space: nowrap !important;
}
</style>
